import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { baseUrl } from '../Data/service/global.service';

export type UnistructUserStatus =
  | 'FILE_CREATED'
  | 'SENDING'
  | 'INI_UPLOADED'
  | 'BKM_UPLOADED'
  | 'PROCESSING'
  | 'RECEIVED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'FAILED_RETRYABLE';

export interface UnistructUiFlags {
  file_created: boolean;
  sending: boolean;
  ini_uploaded: boolean;
  bkm_uploaded: boolean;
  processing: boolean;
  accepted: boolean;
  rejected: boolean;
  can_retry: boolean;
}

export interface UnistructJob {
  job_id: number;
  company_id: number;
  case_number: string | null;
  start_period: string;
  end_period: string;
  record_count: number | null;
  user_status: UnistructUserStatus;
  retryable: boolean;
  rejection_reason: string | null;
  ui: UnistructUiFlags;
  files?: {
    INI?: UnistructFileInfo;
    BKM?: UnistructFileInfo;
  };
}

export interface UnistructFileInfo {
  file_unique_id?: string | null;
  size_bytes?: number | null;
  official_status?: string | null;
  rejection_reason?: string | null;
}

export interface UniformFileRequest {
  company_id: number;
  merchant_id: number;
  start_date: string;
  end_date: string;
  lang: string;
  created_by?: number;
  representor_company_id?: number | null;
}

export type UnistructStepKey = 'created' | 'sending' | 'ini' | 'bkm' | 'review' | 'result';
export type UnistructStepState = 'done' | 'active' | 'pending' | 'error' | 'success';

export interface UnistructStep {
  key: UnistructStepKey;
  state: UnistructStepState;
}

const STEP_KEYS: UnistructStepKey[] = ['created', 'sending', 'ini', 'bkm', 'review', 'result'];
const POLL_STATUSES: UnistructUserStatus[] = ['PROCESSING', 'RECEIVED', 'BKM_UPLOADED'];

@Injectable({ providedIn: 'root' })
export class UnistructService {
  constructor(private http: HttpClient) {}

  submit(body: UniformFileRequest): Observable<UnistructJob> {
    return this.http.post(`${baseUrl}unistruct/submit`, body).pipe(
      map((res) => unwrapJob(res))
    );
  }

  listJobs(companyId: number): Observable<UnistructJob[]> {
    return this.http.get(`${baseUrl}unistruct/jobs`, {
      params: { company_id: String(companyId) },
    }).pipe(map((res) => unwrapJobs(res)));
  }

  refreshStatus(jobId: number): Observable<UnistructJob> {
    return this.http.post(`${baseUrl}unistruct/jobs/${jobId}/refresh-status`, {}).pipe(
      map((res) => unwrapJob(res))
    );
  }

  retry(jobId: number): Observable<UnistructJob> {
    return this.http.post(`${baseUrl}unistruct/jobs/${jobId}/retry`, {}).pipe(
      map((res) => unwrapJob(res))
    );
  }

  downloadZip(body: UniformFileRequest): Observable<HttpResponse<Blob>> {
    return this.http.post(`${baseUrl}zip/create`, body, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  /** OAuth for the uniform file only. Invoice authorization-url is a different scope. */
  authorizationUrl(companyId: number): Observable<{ url: string }> {
    return this.http.get(`${baseUrl}ita-oauth/unistruct-authorization-url`, {
      params: { companyId: String(companyId) },
    }).pipe(map((res) => {
      const record = (res ?? {}) as { url?: string; data?: { url?: string } };
      return { url: record.url || record.data?.url || '' };
    }));
  }
}

/**
 * 401 on the uniform-file APIs is often an ITA security/subscription response,
 * not an expired app session. Those must keep the session and show the link button.
 */
export function isUnistructService401(err: { status?: number; url?: string | null; error?: unknown }): boolean {
  if (err?.status !== 401) {
    return false;
  }
  const url = err.url || '';
  if (!url.includes('/unistruct/') && !url.includes('unistruct-authorization-url')) {
    return false;
  }
  const text = errorBodyText(err.error).toLowerCase();
  if (/security|subscription|scope|unistruct|ملف موح|מבנה אחיד/.test(text)) {
    return true;
  }
  if (/jwt|invalid token|token expired|session expired|unauthorized/.test(text)) {
    return false;
  }
  return true;
}

function errorBodyText(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object') {
    try {
      return JSON.stringify(error);
    } catch {
      return '';
    }
  }
  return '';
}

export function shouldPollUnistructJob(job: UnistructJob): boolean {
  return POLL_STATUSES.includes(job.user_status);
}

export function buildUnistructSteps(job: UnistructJob): UnistructStep[] {
  const ui = normalizeUi(job.ui);
  const status = job.user_status;
  const accepted = ui.accepted || status === 'ACCEPTED';
  const rejected = ui.rejected || status === 'REJECTED';
  const failed = status === 'FAILED_RETRYABLE';

  if (accepted) {
    return STEP_KEYS.map((key, index) => ({
      key,
      state: index === STEP_KEYS.length - 1 ? 'success' : 'done',
    }));
  }

  if (rejected) {
    return STEP_KEYS.map((key, index) => ({
      key,
      state: index === STEP_KEYS.length - 1 ? 'error' : 'done',
    }));
  }

  if (failed) {
    const cursor = !ui.file_created ? 0 : !ui.ini_uploaded ? 1 : !ui.bkm_uploaded ? 3 : 4;
    return STEP_KEYS.map((key, index) => ({
      key,
      state: index < cursor ? 'done' : index === cursor ? 'error' : 'pending',
    }));
  }

  let cursor = -1;
  if (ui.processing || status === 'PROCESSING' || status === 'RECEIVED' || ui.bkm_uploaded || status === 'BKM_UPLOADED') {
    cursor = 4;
  } else if (ui.ini_uploaded || status === 'INI_UPLOADED') {
    cursor = 3;
  } else if (ui.sending || status === 'SENDING') {
    cursor = 1;
  }

  if (cursor < 0) {
    const created = ui.file_created || status === 'FILE_CREATED';
    return STEP_KEYS.map((key, index) => ({
      key,
      state: index === 0 && created ? 'done' : 'pending',
    }));
  }

  return STEP_KEYS.map((key, index) => ({
    key,
    state: index < cursor ? 'done' : index === cursor ? 'active' : 'pending',
  }));
}

function unwrapJob(res: unknown): UnistructJob {
  const root = asRecord(res);
  const data = asRecord(root?.['data'] ?? root);
  const nested = asRecord(data?.['job']);
  const job = data?.['job_id'] != null ? data : nested;
  const httpStatus = Number(root?.['status']);

  if (job?.['job_id'] == null) {
    if (httpStatus && httpStatus !== 200) {
      throw new HttpErrorResponse({
        status: httpStatus,
        error: root,
      });
    }
    return throwMissingJob(root);
  }

  return normalizeJob(job);
}

function throwMissingJob(root: Record<string, unknown> | null): never {
  throw new HttpErrorResponse({
    status: Number(root?.['status']) || 500,
    error: root,
  });
}

function unwrapJobs(res: unknown): UnistructJob[] {
  const root = asRecord(res);
  const data = root?.['data'] ?? root;
  const list = Array.isArray(data)
    ? data
    : Array.isArray(asRecord(data)?.['jobs'])
      ? (asRecord(data)?.['jobs'] as unknown[])
      : [];
  return list
    .map((item) => asRecord(item))
    .filter((item): item is Record<string, unknown> => !!item && item['job_id'] != null)
    .map((item) => normalizeJob(item));
}

function normalizeJob(raw: Record<string, unknown>): UnistructJob {
  return {
    job_id: Number(raw['job_id']),
    company_id: Number(raw['company_id']),
    case_number: raw['case_number'] != null ? String(raw['case_number']) : null,
    start_period: String(raw['start_period'] ?? ''),
    end_period: String(raw['end_period'] ?? ''),
    record_count: raw['record_count'] == null || raw['record_count'] === '' ? null : Number(raw['record_count']),
    user_status: String(raw['user_status'] ?? '') as UnistructUserStatus,
    retryable: !!raw['retryable'],
    rejection_reason: raw['rejection_reason'] != null && String(raw['rejection_reason']).trim()
      ? String(raw['rejection_reason'])
      : null,
    ui: normalizeUi(raw['ui']),
    files: asRecord(raw['files']) as UnistructJob['files'],
  };
}

function normalizeUi(raw: unknown): UnistructUiFlags {
  const ui = asRecord(raw);
  return {
    file_created: !!ui?.['file_created'],
    sending: !!ui?.['sending'],
    ini_uploaded: !!ui?.['ini_uploaded'],
    bkm_uploaded: !!ui?.['bkm_uploaded'],
    processing: !!ui?.['processing'],
    accepted: !!ui?.['accepted'],
    rejected: !!ui?.['rejected'],
    can_retry: !!ui?.['can_retry'],
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

export function readApiMessage(body: unknown, lang: string): string {
  let parsed: unknown = body;
  if (typeof parsed === 'string') {
    const trimmed = parsed.trim();
    if (!trimmed || trimmed.startsWith('<')) {
      return '';
    }
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        return trimmed;
      }
    } else {
      return trimmed;
    }
  }

  const record = asRecord(parsed);
  if (!record) {
    return '';
  }

  const messageAr = stringField(record, 'messageAr');
  const messageHe = stringField(record, 'messageHe');
  const messageEn = stringField(record, 'messageEn');
  const message = stringField(record, 'message');

  if (lang === 'ar') {
    return messageAr || message || messageEn || messageHe;
  }
  if (lang === 'he') {
    return messageHe || message || messageEn || messageAr;
  }
  return messageEn || message || messageAr || messageHe;
}

function stringField(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  return typeof value === 'string' ? value.trim() : '';
}
